import React, { useCallback, useRef, useState } from "react";
import {
  Box,
  HStack,
  Icon,
  IconButton,
  Image,
  Input,
  Text,
  VStack,
} from "@chakra-ui/react";
import { FileText, Link, ImagePlus, Pencil, Plus, X } from "lucide-react";
import { useLanguage } from "@/contexts/languageContext";
import { useApi } from "@/uses/useApi";
import objectUtil from "@/utils/objectUtil";

const RESOURCE_TYPES = [
  { value: "link", label: "Lien", icon: Link },
  { value: "image", label: "Image", icon: ImagePlus },
  { value: "pdf", label: "PDF", icon: FileText },
];

const RessourceReadOnly = ({ ressource, index, onEdit, onRemove }) => {
  const typeInfo = RESOURCE_TYPES.find((t) => t.value === ressource.type);

  const getFilename = (url) => {
    if (!url) return "";
    return url.split("/").pop();
  };

  return (
    <Box
      border="1px solid"
      borderColor="gray.200"
      borderRadius="md"
      p={4}
      w="100%"
      opacity={0.9}
    >
      <HStack justify="space-between">
        <HStack gap={2} flex={1} overflow="hidden">
          {typeInfo && <Icon as={typeInfo.icon} boxSize={5} color="primary.500" />}
          <Text fontWeight="bold" fontSize="sm">
            {ressource.name || typeInfo?.label || "Ressource"}
          </Text>
          <Text fontSize="sm" isTruncated color="gray.500">
            {ressource.type === "link"
              ? ressource.content
              : getFilename(ressource.content)}
          </Text>
        </HStack>
        <HStack gap={1}>
          <IconButton size="sm" variant="ghost" onClick={() => onEdit(index)}>
            <Icon as={Pencil} boxSize={4} />
          </IconButton>
          <IconButton size="sm" variant="ghost" onClick={() => onRemove(index)}>
            <Icon as={X} boxSize={4} />
          </IconButton>
        </HStack>
      </HStack>
    </Box>
  );
};

const RessourceForm = ({ ressource, index, onChange, onRemove }) => {
  const api = useApi();
  const fileInputRef = useRef();

  const handleChange = (field, value) => {
    onChange(index, { ...ressource, [field]: value });
  };

  const handleFileUpload = useCallback((e) => {
    const files = e.target.files;
    if (!files || files.length !== 1) return;

    api.FileService.getPresignedURL(files).then((res) => {
      if (res?.data?.type === "success") {
        if (!objectUtil.isArray(res?.data?.value) || !res?.data?.value?.[0]?.presignedURL) {
          throw new Error("MISSING_PRESIGNED_URL");
        }
        if (!objectUtil.isArray(res?.data?.value) || !res?.data?.value?.[0]?.url) {
          throw new Error("MISSING_FILE_URL");
        }
        api.FileService.upload(res.data.value[0].presignedURL, files.item(0)).then(() => {
          onChange(index, {
            ...ressource,
            content: res.data.value[0].url,
          });
        }).catch(console.error);
      }
    }).catch(console.error);
  }, [ressource, index, onChange, api]);

  const getFilename = (url) => {
    if (!url) return "";
    return url.split("/").pop();
  };

  return (
    <Box
      border="1px solid"
      borderColor="gray.200"
      borderRadius="md"
      p={4}
      w="100%"
    >
      <HStack justify="space-between" mb={3}>
        <Text fontWeight="bold">Ressource {index + 1}</Text>
        <IconButton
          size="sm"
          variant="ghost"
          onClick={() => onRemove(index)}
        >
          <Icon as={X} boxSize={4} />
        </IconButton>
      </HStack>

      <VStack gap={3} align="stretch">
        <Box>
          <Text mb={1} fontSize="sm">Type</Text>
          <HStack gap={2}>
            {RESOURCE_TYPES.map((type) => (
              <Box
                key={type.value}
                as="button"
                type="button"
                px={4}
                py={2}
                borderRadius="md"
                border="1px solid"
                borderColor={ressource.type === type.value ? "primary.500" : "gray.200"}
                bg={ressource.type === type.value ? "primary.500" : "transparent"}
                color={ressource.type === type.value ? "white" : "inherit"}
                cursor="pointer"
                display="flex"
                alignItems="center"
                gap={2}
                onClick={() => handleChange("type", type.value)}
              >
                <Icon as={type.icon} boxSize={4} />
                {type.label}
              </Box>
            ))}
          </HStack>
        </Box>

        <Box>
          <Text mb={1} fontSize="sm">Nom</Text>
          <Input
            placeholder="Nom de la ressource"
            value={ressource.name || ""}
            onChange={(e) => handleChange("name", e.target.value)}
          />
        </Box>

        {ressource.type === "link" && (
          <Box>
            <Text mb={1} fontSize="sm">URL</Text>
            <Input
              placeholder="https://..."
              value={ressource.content || ""}
              onChange={(e) => handleChange("content", e.target.value)}
            />
          </Box>
        )}

        {ressource.type === "image" && (
          <Box>
            <Input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileUpload}
              display="none"
            />
            {ressource.content ? (
              <VStack align="stretch" gap={2}>
                <Image
                  src={ressource.content}
                  alt={`Ressource ${index + 1}`}
                  maxH="150px"
                  objectFit="contain"
                  borderRadius="md"
                />
                <HStack>
                  <Text fontSize="sm" isTruncated flex={1}>
                    {getFilename(ressource.content)}
                  </Text>
                  <IconButton
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      handleChange("content", null);
                      if (fileInputRef.current) fileInputRef.current.value = "";
                    }}
                  >
                    <Icon as={X} boxSize={4} />
                  </IconButton>
                </HStack>
              </VStack>
            ) : (
              <IconButton
                onClick={() => fileInputRef.current?.click()}
                backgroundColor="secondary.500"
                width="100%"
              >
                <Icon as={ImagePlus} boxSize={5} />
                <Text ml={2}>Choisir une image</Text>
              </IconButton>
            )}
          </Box>
        )}

        {ressource.type === "pdf" && (
          <Box>
            <Input
              ref={fileInputRef}
              type="file"
              accept=".pdf"
              onChange={handleFileUpload}
              display="none"
            />
            {ressource.content ? (
              <HStack
                p={3}
                border="1px solid"
                borderColor="gray.200"
                borderRadius="md"
              >
                <Icon as={FileText} boxSize={5} color="primary.500" />
                <Text fontSize="sm" isTruncated flex={1}>
                  {getFilename(ressource.content)}
                </Text>
                <IconButton
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    handleChange("content", null);
                    if (fileInputRef.current) fileInputRef.current.value = "";
                  }}
                >
                  <Icon as={X} boxSize={4} />
                </IconButton>
              </HStack>
            ) : (
              <IconButton
                onClick={() => fileInputRef.current?.click()}
                backgroundColor="secondary.500"
                width="100%"
              >
                <Icon as={FileText} boxSize={5} />
                <Text ml={2}>Choisir un PDF</Text>
              </IconButton>
            )}
          </Box>
        )}
      </VStack>
    </Box>
  );
};

export const RessourcesInput = ({ ressources = [], onChange, error }) => {
  const { t } = useLanguage();
  const [editingIndices, setEditingIndices] = useState(new Set());

  const isExisting = (ressource) => {
    return ressource.name && ressource.type && ressource.content;
  };

  const addRessource = () => {
    const newIndex = ressources.length;
    setEditingIndices((prev) => new Set(prev).add(newIndex));
    onChange([...ressources, { type: "", content: null, name: "" }]);
  };

  const updateRessource = (index, updated) => {
    const next = [...ressources];
    next[index] = updated;
    onChange(next);
  };

  const removeRessource = (index) => {
    setEditingIndices((prev) => {
      const next = new Set();
      prev.forEach((i) => {
        if (i < index) next.add(i);
        else if (i > index) next.add(i - 1);
      });
      return next;
    });
    onChange(ressources.filter((_, i) => i !== index));
  };

  const enableEdit = (index) => {
    setEditingIndices((prev) => new Set(prev).add(index));
  };

  return (
    <VStack gap={4} align="stretch" w="100%">
      {ressources.map((ressource, index) => {
        const editing = editingIndices.has(index) || !isExisting(ressource);
        return editing ? (
          <RessourceForm
            key={index}
            index={index}
            ressource={ressource}
            onChange={updateRessource}
            onRemove={removeRessource}
          />
        ) : (
          <RessourceReadOnly
            key={index}
            index={index}
            ressource={ressource}
            onEdit={enableEdit}
            onRemove={removeRessource}
          />
        );
      })}
      <IconButton
        onClick={addRessource}
        backgroundColor="secondary.500"
        width={256}
      >
        <Text>{t("sentences.selectFiles")}</Text>
        <Icon as={Plus} color="gray.550" boxSize={5} />
      </IconButton>
      {error && (
        <Text color="red.500" fontSize="sm">{error}</Text>
      )}
    </VStack>
  );
};

export default RessourcesInput;
